import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import PageHeader, { DarkPage } from "@/components/app/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import SimpleMD from "@/components/app/SimpleMD";
import { Plus, Sparkles, Trash2, ArrowLeft, Calendar, Users } from "lucide-react";
import { toast } from "sonner";

const Meetings = () => {
  const { projectId } = useParams();
  const [project, setProject] = useState(null);
  const [meetings, setMeetings] = useState([]);
  const [selected, setSelected] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", meeting_date: new Date().toISOString().slice(0, 10), attendees: "", agenda: "", notes: "" });
  const [busySummary, setBusySummary] = useState(false);

  const loadAll = async () => {
    const [p, m] = await Promise.all([
      api.get(`/projects/${projectId}`),
      api.get(`/meetings?project_id=${projectId}`),
    ]);
    setProject(p.data);
    setMeetings(m.data);
    if (m.data[0] && !selected) setSelected(m.data[0]);
  };

  useEffect(() => { loadAll(); }, [projectId]); // eslint-disable-line

  const create = async () => {
    if (!form.title.trim()) return;
    const payload = {
      project_id: projectId,
      title: form.title,
      meeting_date: form.meeting_date,
      attendees: form.attendees.split(",").map((a) => a.trim()).filter(Boolean),
      agenda: form.agenda,
      notes: form.notes,
    };
    const { data } = await api.post("/meetings", payload);
    setMeetings([data, ...meetings]);
    setSelected(data);
    setForm({ title: "", meeting_date: new Date().toISOString().slice(0, 10), attendees: "", agenda: "", notes: "" });
    setOpen(false);
    toast.success("Meeting added");
  };

  const updateNotes = async (notes) => {
    if (!selected) return;
    const next = { ...selected, notes };
    setSelected(next);
    setMeetings((prev) => prev.map((m) => (m.meeting_id === selected.meeting_id ? next : m)));
    try {
      await api.patch(`/meetings/${selected.meeting_id}`, { notes });
    } catch {
      toast.error("Failed to save notes");
    }
  };

  const summarise = async () => {
    if (!selected) return;
    if (!(selected.notes || "").trim()) {
      toast.error("Add some notes first");
      return;
    }
    setBusySummary(true);
    try {
      const { data } = await api.post("/ai/meeting-summary", { meeting_id: selected.meeting_id });
      const updated = { ...selected, ai_summary: data.ai_summary, action_items: data.action_items };
      setSelected(updated);
      setMeetings((prev) => prev.map((m) => (m.meeting_id === selected.meeting_id ? updated : m)));
      toast.success("AI summary ready");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't summarise");
    } finally {
      setBusySummary(false);
    }
  };

  const remove = async (id) => {
    await api.delete(`/meetings/${id}`);
    const next = meetings.filter((m) => m.meeting_id !== id);
    setMeetings(next);
    if (selected?.meeting_id === id) setSelected(next[0] || null);
    toast.success("Meeting deleted");
  };

  const inputCls = "rounded-none bg-[#030712] border-[#374151] text-white placeholder:text-[#4B5563] focus:ring-[#00F5D4]";
  const labelCls = "text-[11px] uppercase tracking-[0.2em] text-[#9CA3AF]";

  return (
    <DarkPage>
      <PageHeader
        backTo={`/workspace/${projectId}`}
        eyebrow={project ? `Meeting Hub · ${project.name}` : "Meeting Hub"}
        title="Meetings & Notes"
        description="Capture agendas, take notes in real time, and let AI extract action items."
        actions={
          <div className="flex gap-2">
            <Link to={`/workspace/${projectId}`}>
              <Button variant="outline" className="rounded-none border-[#374151] text-[#9CA3AF] hover:text-white bg-transparent" data-testid="back-to-project-from-meetings">
                <ArrowLeft className="w-4 h-4 mr-1" /> Project
              </Button>
            </Link>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button className="rounded-none bg-[#00F5D4] hover:bg-[#00D4B8] text-[#030712] font-semibold" data-testid="new-meeting-btn">
                  <Plus className="w-4 h-4 mr-1" /> New meeting
                </Button>
              </DialogTrigger>
              <DialogContent className="rounded-none bg-[#111827] border-[#374151] text-white sm:max-w-lg">
                <DialogHeader><DialogTitle className="font-display tracking-tight text-white">New meeting</DialogTitle></DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label className={labelCls}>Title</Label>
                    <Input className={`${inputCls} mt-1`} data-testid="meeting-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className={labelCls}>Date</Label>
                      <Input type="date" className={`${inputCls} mt-1`} data-testid="meeting-date-input" value={form.meeting_date} onChange={(e) => setForm({ ...form, meeting_date: e.target.value })} />
                    </div>
                    <div>
                      <Label className={labelCls}>Attendees (comma-sep)</Label>
                      <Input className={`${inputCls} mt-1`} data-testid="meeting-attendees-input" placeholder="Alice, Bob" value={form.attendees} onChange={(e) => setForm({ ...form, attendees: e.target.value })} />
                    </div>
                  </div>
                  <div>
                    <Label className={labelCls}>Agenda</Label>
                    <Textarea className={`${inputCls} mt-1 min-h-[80px]`} data-testid="meeting-agenda-input" value={form.agenda} onChange={(e) => setForm({ ...form, agenda: e.target.value })} />
                  </div>
                  <div>
                    <Label className={labelCls}>Initial notes (optional)</Label>
                    <Textarea className={`${inputCls} mt-1 min-h-[80px]`} data-testid="meeting-notes-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
                  </div>
                </div>
                <DialogFooter>
                  <Button onClick={create} className="rounded-none bg-[#00F5D4] hover:bg-[#00D4B8] text-[#030712] font-semibold" data-testid="confirm-create-meeting">Create</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        }
      />

      <div className="grid grid-cols-12 min-h-[70vh]">
        <aside className="col-span-12 lg:col-span-4 border-r border-[#1F2937]">
          <div className="px-6 py-3 border-b border-[#1F2937] text-[11px] uppercase tracking-[0.25em] text-[#6B7280]">All meetings</div>
          {meetings.length === 0 && (
            <div className="p-8 text-sm text-[#6B7280]">No meetings yet. Create your first one →</div>
          )}
          {meetings.map((m) => (
            <button
              key={m.meeting_id}
              onClick={() => setSelected(m)}
              className={`w-full text-left px-6 py-4 border-b border-[#1F2937] hover:bg-[#111827] transition-colors ${
                selected?.meeting_id === m.meeting_id
                  ? "bg-[#111827] border-l-2 border-l-[#00F5D4]"
                  : "border-l-2 border-l-transparent"
              }`}
              data-testid={`meeting-item-${m.meeting_id}`}
            >
              <div className="font-medium text-sm line-clamp-1">{m.title}</div>
              <div className="text-[11px] text-[#6B7280] mt-1 flex items-center gap-2">
                <Calendar className="w-3 h-3" /> {new Date(m.meeting_date).toLocaleDateString()}
                {m.attendees?.length > 0 && (<><Users className="w-3 h-3 ml-2" /> {m.attendees.length}</>)}
              </div>
              {m.action_items?.length > 0 && (
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#00F5D4] mt-2">{m.action_items.length} action items</div>
              )}
            </button>
          ))}
        </aside>

        <section className="col-span-12 lg:col-span-8 p-8">
          {!selected ? (
            <div className="text-sm text-[#6B7280]">Select a meeting to view notes.</div>
          ) : (
            <div className="space-y-6">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="text-[11px] uppercase tracking-[0.25em] text-[#00F5D4]">
                    {new Date(selected.meeting_date).toLocaleDateString(undefined, { weekday: "short", year: "numeric", month: "short", day: "numeric" })}
                  </div>
                  <h2 className="font-display font-black text-3xl tracking-tighter mt-2">{selected.title}</h2>
                  {selected.attendees?.length > 0 && (
                    <div className="text-sm text-[#9CA3AF] mt-2">Attendees: {selected.attendees.join(", ")}</div>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button onClick={summarise} disabled={busySummary} className="rounded-none bg-[#00F5D4] hover:bg-[#00D4B8] text-[#030712] font-semibold" data-testid="ai-summarise-btn">
                    <Sparkles className="w-4 h-4 mr-1" /> {busySummary ? "Summarising…" : "AI Summary"}
                  </Button>
                  <Button onClick={() => remove(selected.meeting_id)} variant="outline" className="rounded-none border-[#374151] text-[#6B7280] hover:text-[#FF007A] bg-transparent" data-testid="delete-meeting-btn">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {selected.agenda && (
                <div className="border border-[#1F2937] bg-[#111827] p-5">
                  <div className="text-[11px] uppercase tracking-[0.25em] text-[#6B7280] mb-2">Agenda</div>
                  <div className="text-sm whitespace-pre-wrap text-[#9CA3AF]">{selected.agenda}</div>
                </div>
              )}

              <div className="border border-[#1F2937]">
                <div className="px-5 py-3 border-b border-[#1F2937] text-[11px] uppercase tracking-[0.25em] text-[#6B7280]">Notes (auto-saves)</div>
                <Textarea
                  className="rounded-none border-0 bg-[#111827] text-white min-h-[260px] focus-visible:ring-0 resize-y placeholder:text-[#4B5563]"
                  placeholder="Capture discussion, decisions, blockers… anything you want the AI to summarise later."
                  value={selected.notes || ""}
                  onChange={(e) => updateNotes(e.target.value)}
                  data-testid="meeting-notes-editor"
                />
              </div>

              {selected.ai_summary && (
                <div className="border border-[#374151] bg-[#111827] p-6" data-testid="meeting-ai-summary">
                  <div className="text-[11px] uppercase tracking-[0.25em] text-[#00F5D4] mb-3">AI Summary</div>
                  <SimpleMD text={selected.ai_summary} />
                </div>
              )}

              {selected.action_items?.length > 0 && (
                <div className="border border-[#1F2937]" data-testid="meeting-action-items">
                  <div className="px-5 py-3 border-b border-[#1F2937] text-[11px] uppercase tracking-[0.25em] text-[#6B7280]">Action items</div>
                  <table className="w-full text-sm">
                    <thead className="bg-[#1F2937] text-[10px] uppercase tracking-[0.2em] text-[#6B7280]">
                      <tr>
                        <th className="text-left p-3 font-semibold">Task</th>
                        <th className="text-left p-3 font-semibold">Owner</th>
                        <th className="text-left p-3 font-semibold">Due</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selected.action_items.map((a, i) => (
                        <tr key={`${a.task}-${i}`} className="border-t border-[#1F2937]">
                          <td className="p-3">{a.task}</td>
                          <td className="p-3 text-[#9CA3AF]">{a.owner || "—"}</td>
                          <td className="p-3 font-mono text-[#9CA3AF]">{a.due || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </section>
      </div>
    </DarkPage>
  );
};

export default Meetings;
